import { useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useMatch, useNavigate, useParams } from 'react-router';
import { useChat } from './store/chat';
import { ChatList } from './pages/ChatList';
import { ChatView } from './pages/ChatView';
import { SettingsPage } from './pages/Settings';
import { ForgotPasswordPage, LoginPage, ProfileSetupPage, RegisterPage } from './pages/AuthPages';
import { CallOverlay } from './calls/CallOverlay';
import { handleCallLink } from './calls/callManager';
import { ConnectionBanner } from './components/Banners';
import { UpdatePrompt } from './components/UpdatePrompt';
import { MediaHelp } from './components/MediaHelp';

function RequireAuth() {
  const me = useChat((s) => s.me);
  return me ? <Outlet /> : <Navigate to="/login" replace />;
}

function GuestOnly() {
  const me = useChat((s) => s.me);
  // Right after registration the user is offered the profile setup (it can be skipped).
  return me ? <Navigate to={me.hasName ? '/' : '/setup'} replace /> : <Outlet />;
}

/** Two panes on wide screens; one pane (list or chat) on phones. */
function MainLayout() {
  const inChat = useMatch('/c/:id');
  const inSettings = useMatch('/settings');
  const detail = Boolean(inChat || inSettings);
  return (
    <div className={`layout${detail ? ' show-detail' : ''}`}>
      <ChatList />
      <main className="detail">
        <Outlet />
      </main>
      <CallOverlay />
    </div>
  );
}

/** A fresh ChatView per conversation: scroll position and anchors must not carry over between chats. */
function ChatRoute() {
  const { id } = useParams();
  return <ChatView key={id} />;
}

function Placeholder() {
  return (
    <div className="placeholder">
      <img src="/icons/icon-192.png" alt="" width={72} height={72} />
      <p>Выберите чат или найдите собеседника</p>
    </div>
  );
}

/** Notification clicks are forwarded by the service worker as navigation requests. */
function SwNavigation() {
  const nav = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (e: MessageEvent) => {
      if (e.data?.type === 'navigate' && typeof e.data.url === 'string' && e.data.url.startsWith('/')) nav(handleCallLink(e.data.url));
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [nav]);
  return null;
}

export function App() {
  const authChecked = useChat((s) => s.authChecked);
  // The boot preloader from index.html stays up until the first real screen is ready, then fades out.
  useEffect(() => {
    if (!authChecked) return;
    const boot = document.getElementById('boot');
    if (!boot) return;
    requestAnimationFrame(() => boot.classList.add('hide'));
    const t = setTimeout(() => boot.remove(), 600);
    return () => clearTimeout(t);
  }, [authChecked]);
  if (!authChecked) return null;
  return (
    <BrowserRouter>
      <SwNavigation />
      <UpdatePrompt />
      <MediaHelp />
      <ConnectionBanner />
      <Routes>
        <Route element={<GuestOnly />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot" element={<ForgotPasswordPage />} />
        </Route>
        <Route element={<RequireAuth />}>
          <Route path="/setup" element={<ProfileSetupPage />} />
          <Route element={<MainLayout />}>
            <Route index element={<Placeholder />} />
            <Route path="/c/:id" element={<ChatRoute />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
